import { useEffect, useRef } from 'react';

import type { NoteTelemetryEventType,SelectionMetrics } from '../utils/analytics/noteTelemetry';

export type DomAnalyticsHookOptions = {
  docId: string;
  getDocVersion: () => string;
  getPlainText: () => string;
  settleMs?: number;
  throttleMs?: number;
  metadata?: {
    module_id?: string;
    section_id?: string;
    note_id?: string;
  };
  onEvent: (type: NoteTelemetryEventType, payload: SelectionMetrics) => void;
};

/**
 * Hook to track DOM-based selection and copy analytics for plain text content
 * Works with rendered paragraphs/divs containing text content
 */
export function useDomSelectionAnalytics(
  rootRef: React.RefObject<HTMLElement>,
  opts: DomAnalyticsHookOptions,
) {
  const settleMs = opts.settleMs ?? 1000; // 1 second default
  const throttleMs = opts.throttleMs ?? 100;

  // Running state for a selection gesture
  const state = useRef<{
    selectionId?: string;
    startedAt?: number;
    lastChangedAt?: number;
    lastLen: number;
    settleTimer?: number;
    lastInput?: 'mouse' | 'touch' | 'keyboard';
  }>({ lastLen: 0 });

  const now = () => performance.now();
  const uuid = () => crypto.randomUUID();

  // Check if selection is backward
  const isBackward = (): boolean | undefined => {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) return undefined;

    try {
      const range = document.createRange();
      range.setStart(sel.anchorNode!, sel.anchorOffset);
      range.setEnd(sel.focusNode!, sel.focusOffset);
      return (
        range.collapsed &&
        (sel.anchorNode !== sel.focusNode || sel.anchorOffset !== sel.focusOffset)
      );
    } catch {
      return undefined;
    }
  };

  // Modality tracker (consistent across contexts): pointerdown + keydown
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const onPointerDown = (e: PointerEvent) => {
      state.current.lastInput = e.pointerType === 'touch' ? 'touch' : 'mouse';
    };
    const onKeyDown = () => {
      state.current.lastInput = 'keyboard';
    };

    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('keydown', onKeyDown);

    return () => {
      root.removeEventListener('pointerdown', onPointerDown);
      root.removeEventListener('keydown', onKeyDown);
    };
  }, [rootRef]);

  // Throttle helper
  const lastFire = useRef(0);
  const shouldThrottle = () => {
    const t = now();
    if (t - lastFire.current < throttleMs) return true;
    lastFire.current = t;
    return false;
  };

  // Selection change handler
  useEffect(() => {
    const handleSelectionChange = async () => {
      if (!rootRef.current || shouldThrottle()) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        // Selection cleared
        if (state.current.startedAt) {
          state.current.selectionId = undefined;
          state.current.startedAt = undefined;
          state.current.lastChangedAt = undefined;
          if (state.current.settleTimer) {
            clearTimeout(state.current.settleTimer);
          }
          state.current.settleTimer = undefined;
        }
        state.current.lastLen = 0;
        return;
      }

      // Check if selection is within our root element
      const range = selection.getRangeAt(0);
      if (!rootRef.current.contains(range.commonAncestorContainer)) {
        return; // Selection is outside our element
      }

      const selectedText = selection.toString();
      const len = selectedText.length;
      const t = now();

      // Start of a new selection gesture
      if (len > 0 && !state.current.startedAt) {
        state.current.selectionId = uuid();
        state.current.startedAt = t;
      }

      // Update timing + (re)arm settle timer
      if (len > 0) {
        state.current.lastChangedAt = t;
        if (state.current.settleTimer) {
          clearTimeout(state.current.settleTimer);
        }

        state.current.settleTimer = window.setTimeout(async () => {
          if (!state.current.startedAt || !rootRef.current) return;

          // Re-check selection when timer fires
          const currentSelection = window.getSelection();
          if (!currentSelection || currentSelection.rangeCount === 0) return;

          const currentRange = currentSelection.getRangeAt(0);
          if (!rootRef.current.contains(currentRange.commonAncestorContainer)) return;

          const currentSelectedText = currentSelection.toString();
          const currentLen = currentSelectedText.length;
          if (currentLen === 0) return;

          const settleTime = now();
          const plainText = opts.getPlainText();

          const payload: SelectionMetrics = {
            selectionId: state.current.selectionId!,
            docId: opts.docId,
            docVersion: opts.getDocVersion(),
            chars: {
              selected: currentLen,
              total: plainText.length,
              percent: plainText.length ? currentLen / plainText.length : 0,
            },
            timings: {
              startedAt: state.current.startedAt,
              lastChangedAt: state.current.lastChangedAt!,
              settledAt: settleTime,
              msToSettle: settleTime - state.current.startedAt,
            },
            modality: state.current.lastInput,
            isBackward: isBackward(),
            ua: navigator.userAgent,
            metadata: opts.metadata,
          };

          opts.onEvent('text_selected', payload);
        }, settleMs);
      }

      state.current.lastLen = len;
    };

    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (state.current.settleTimer) {
        clearTimeout(state.current.settleTimer);
      }
    };
  }, [rootRef, opts, settleMs, throttleMs]);

  // Copy / cut handlers
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const handler = (type: 'text_copied') => async () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (!root.contains(range.commonAncestorContainer)) return;

      const selTxt = selection.toString();
      const plainText = opts.getPlainText();
      const t = now();

      const payload: SelectionMetrics = {
        selectionId: state.current.selectionId ?? uuid(),
        docId: opts.docId,
        docVersion: opts.getDocVersion(),
        chars: {
          selected: selTxt.length,
          total: plainText.length,
          percent: plainText.length ? selTxt.length / plainText.length : 0,
        },
        timings: {
          startedAt: state.current.startedAt ?? t,
          lastChangedAt: state.current.lastChangedAt ?? t,
          copiedAt: t,
          msToCopy: state.current.startedAt ? t - state.current.startedAt : 0,
        },
        modality: state.current.lastInput,
        isBackward: isBackward(),
        ua: navigator.userAgent,
        metadata: opts.metadata,
      };

      opts.onEvent(type, payload);
    };

    const onCopy = () => handler('text_copied')();
    const onCut = () => handler('text_copied')();

    root.addEventListener('copy', onCopy);
    root.addEventListener('cut', onCut);

    return () => {
      root.removeEventListener('copy', onCopy);
      root.removeEventListener('cut', onCut);
    };
  }, [rootRef, opts]);
}
